import { useEffect, useMemo, useState } from 'react';
import { toast as message } from 'sonner';
import { useGlobalMetadata } from '../../utils/providers.js';
import { useMyCapabilities } from '../../utils/metadata/capabilities.js';
import { callRpc, updateRecord } from '../../utils/supabase-operations.js';
import { invalidateAndRefreshAllQueriesForTable } from '../../utils/metadata/cache.js';
import { queryClient } from '../../utils/constants.js';
import { REPORT_ROWS_QUERY_KEY } from './reportGridDataSource.js';
import { requestReportGridRefresh } from '../../utils/reportGridRefresh.js';
import './LivePicklistCell.css';

/**
 * Where a live dropdown writes (REG-740). Comes from a report field's ui_data
 * (editable + edit_always, edit_table, edit_rpc, edit_rpc_id_param,
 * edit_capability) or, on the code-defined rep / funder tabs, from
 * MERCHANT_FEEDBACK_EDIT below.
 */
export interface LivePicklistTarget {
  /** Table the field belongs to; its picklist supplies the options. */
  table: string;
  field: string;
  /** Capability that makes the dropdown live; without it the value is plain text. */
  capability: string | null;
  /** Write through this public RPC as (idParam, p_value) instead of a row update. */
  rpc: string | null;
  idParam: string;
}

/** Approvals Merchant Feedback (REG-740): same target as the primary_approvals report field. */
export const MERCHANT_FEEDBACK_EDIT: LivePicklistTarget = {
  table: 'merchant',
  field: 'merchant_response',
  capability: 'edit_merchant_feedback',
  rpc: 'set_merchant_feedback',
  idParam: 'p_merchant',
};

export interface LivePicklistCellParams {
  target: LivePicklistTarget;
  /** Row id of the record the value belongs to (the merchant for feedback). */
  recordId: number | string | null | undefined;
  value: string | null | undefined;
  label: string;
  /** Called after a save or an undo, so the grid can refetch its rows. */
  onSaved?: () => void | Promise<void>;
}

async function writeValue(target: LivePicklistTarget, recordId: number | string, value: string | null, schema: string) {
  if (target.rpc) {
    await callRpc({ schema: 'public', functionName: target.rpc, params: { [target.idParam]: recordId, p_value: value } });
    return;
  }
  const { error } = await updateRecord({
    tableName: target.table,
    schema,
    data: { [target.field]: value },
    whereClause: { id: recordId },
    options: { fieldName: target.field },
  });
  if (error) throw error;
}

/**
 * A picklist value that is always editable in place: no Edit mode, one pick
 * saves. Options are the field's picklist from metadata, so values added,
 * renamed, reordered or retired in Admin Console > Tables show up without a
 * deploy. Saved toasts carry an Undo that writes the previous value back the
 * same way.
 */
export function LivePicklistCell({ target, recordId, value, label, onSaved }: LivePicklistCellParams) {
  const { data: metadata } = useGlobalMetadata();
  const { hasCapability, isLoading } = useMyCapabilities();
  const [current, setCurrent] = useState<string | null>(value ?? null);
  const [saving, setSaving] = useState(false);
  useEffect(() => setCurrent(value ?? null), [value]);

  const fieldMeta = (metadata as any)?.fieldData?.[target.table]?.byFieldName?.[target.field];
  const schema = (metadata as any)?.tableData?.byName?.[target.table]?.schema_name ?? 'public';
  const options = useMemo(() => {
    const values: Array<{ value: string; place?: number }> = Array.isArray(fieldMeta?.picklist?.values)
      ? fieldMeta.picklist.values
      : [];
    return [...values]
      .filter((item) => item?.value)
      .sort((a, b) => (a.place ?? 0) - (b.place ?? 0))
      .map((item) => item.value);
  }, [fieldMeta]);

  const canEdit = !isLoading && recordId != null && options.length > 0
    && (!target.capability || hasCapability(target.capability));
  if (!canEdit) return <span className="live-picklist-text">{current || ''}</span>;

  const save = async (next: string | null, previous: string | null, undo = false) => {
    setSaving(true);
    setCurrent(next);
    try {
      await writeValue(target, recordId as number | string, next, schema);
      await Promise.all([
        invalidateAndRefreshAllQueriesForTable(target.table),
        invalidateAndRefreshAllQueriesForTable('submission'),
        queryClient.invalidateQueries({ queryKey: [REPORT_ROWS_QUERY_KEY] }),
      ]);
      requestReportGridRefresh();
      await onSaved?.();
      if (undo) {
        message.success(`Undone: ${label} back to ${next ?? "blank"}`);
      } else {
        message.success('Saved', {
          action: { label: 'Undo', onClick: () => void save(previous, next, true) },
        });
      }
    } catch (error) {
      setCurrent(previous);
      message.error(error instanceof Error ? error.message : `Could not save ${label}`);
    } finally {
      setSaving(false);
    }
  };

  // A retired value still on the record stays visible as the current choice.
  const shown = current && !options.includes(current) ? [current, ...options] : options;
  return (
    <select
      className={`live-picklist-select${current ? '' : ' live-picklist-select--empty'}`}
      aria-label={label}
      value={current ?? ''}
      disabled={saving}
      onClick={(event) => event.stopPropagation()}
      onChange={(event) => void save(event.target.value || null, current)}
    >
      <option value="">{current ? 'Clear' : 'Select'}</option>
      {shown.map((option) => <option key={option} value={option}>{option}</option>)}
    </select>
  );
}
